import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeProvider } from "@mui/material";
import Footer from "components/HomePage/Footer";
import Header from "components/HomePage/Header";
import {
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  User as UserIcon,
} from "lucide-react";
import { Territory } from "models/territory";
import { User } from "models/user";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosHttpClient } from "../../services/AxiosHttpService";
import { CustomTheme } from "../../styles/Theme";
import AutocompleteInfiniteScroll from "../Reusable/AutocompleteInfiniteScroll";
import SelectLabeled from "../Reusable/SelectLabeled";
import TextFieldLabeled from "../Reusable/TextFieldLabeled";

interface FormErrors {
  title?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  confirmPassword?: string;
  phoneNumber?: string;
  professionalPosition?: string;
  location?: string;
  acceptTerms?: string;
  activation?: string;
}

const InviteActivation: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  // Form state
  const [inputTitle, setInputTitle] = useState("");
  const [inputFirstName, setInputFirstName] = useState("");
  const [inputLastName, setInputLastName] = useState("");
  const [inputPhoneNumber, setInputPhoneNumber] = useState("");
  const [inputProfessionalPosition, setInputProfessionalPosition] =
    useState("");
  const [inputLocation, setInputLocation] = useState<Territory | null>(null);
  const [inputLocationText, setInputLocationText] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [inputConfirmPassword, setInputConfirmPassword] = useState("");
  const [inputLanguage, setInputLanguage] = useState("fr");
  const [inputAcceptTerms, setInputAcceptTerms] = useState(false);

  // UI state
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userData, setUserData] = useState<User | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState(true);

  // Territory management
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [isLoadingTerritories, setIsLoadingTerritories] = useState(false);
  const [page, setPage] = useState(1);
  const [totalNumberOfTerritories, setTotalNumberOfTerritories] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const previousController = useRef<AbortController>(null!);

  // API functions for territories
  const getTerritoriesPage = async (pageNumber?: number) => {
    if (previousController.current) {
      previousController.current.abort();
    }
    setIsLoadingTerritories(true);
    const controller = new AbortController();
    const signal = controller.signal;
    previousController.current = controller;
    try {
      const res = await AxiosHttpClient.getForNonAuth<any>(
        "api/v1/docuveritas/territories/page",
        {
          criteriaList:
            "name=" +
            inputLocationText +
            ",featureClass=P,population=5000=greaterThanEqual",
          pageNumber: (pageNumber ? pageNumber : page) - 1,
          pageSize: 20,
          sort: "name",
          order: "asc",
        },
        signal
      );
      setTotalNumberOfTerritories(Number(res.totalElements));
      (pageNumber ? pageNumber : page) === 1
        ? setTerritories([...res.content])
        : setTerritories([...territories, ...res.content]);
      setPage((pageNumber ? pageNumber : page) === 1 ? 2 : page + 1);
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoadingTerritories(false);
    }
  };

  const fetchMoreData = async (): Promise<Territory[]> => {
    if (page > totalNumberOfTerritories / 20) {
      await getTerritoriesPage(page);
      setHasMore(false);
      return territories;
    }
    await getTerritoriesPage(page);
    return territories;
  };

  // Validate invitation token
  useEffect(() => {
    if (!uuid) {
      setErrors({ activation: "Token d'invitation manquant ou invalide" });
      setIsValidatingToken(false);
      return;
    }

    // Validate the invitation uuid
    AxiosHttpClient.getForNonAuth<User>(
      `api/v1/docuveritas/users/validate/${uuid}`
    )
      .then((response) => {
        setUserData(response);
        setIsValidatingToken(false);
      })
      .catch((error) => {
        console.error("Error validating invitation uuid:", error);
        setErrors({
          activation: "Le lien d'invitation est invalide ou a expiré",
        });
        setIsValidatingToken(false);
      });
  }, [uuid]);

  useEffect(() => {
    if (userData) {
      setInputFirstName(userData.firstName || "");
      setInputLastName(userData.lastName || "");
    }
  }, [userData]);

  // Initialize territories
  useEffect(() => {
    setHasMore(true);
    getTerritoriesPage(1);
  }, [inputLocationText]);

  // Helper functions
  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!inputFirstName) newErrors.firstName = "Le prénom est requis";
    if (!inputLastName) newErrors.lastName = "Le nom est requis";
    if (!inputPhoneNumber) newErrors.phoneNumber = "Le téléphone est requis";
    if (!inputProfessionalPosition)
      newErrors.professionalPosition =
        "La position professionnelle est requise";
    if (!inputLocation) newErrors.location = "La localisation est requise";

    if (!inputPassword) {
      newErrors.password = "Le mot de passe est requis";
    } else if (inputPassword.length < 8) {
      newErrors.password =
        "Le mot de passe doit contenir au moins 8 caractères";
    }

    if (inputPassword !== inputConfirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }

    if (!inputAcceptTerms) {
      newErrors.acceptTerms =
        "Vous devez accepter les conditions d'utilisation";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !uuid || !userData) return;

    setIsLoading(true);

    try {
      await AxiosHttpClient.putForNonAuth(
        `api/v1/docuveritas/users/activate/${uuid}`,
        {
          title: inputTitle,
          firstName: inputFirstName,
          lastName: inputLastName,
          phoneNumber: inputPhoneNumber,
          professionalPosition: inputProfessionalPosition,
          location: inputLocation?.id,
          password: inputPassword,
          language: inputLanguage,
        }
      );

      setSuccess(true);
    } catch (error) {
      console.error("Error activating account:", error);
      setErrors({
        activation:
          "Une erreur s'est produite lors de l'activation du compte. Veuillez réessayer.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <ThemeProvider theme={CustomTheme}>
        <div className="min-h-screen bg-white">
          <Header />
          <main className="py-20 bg-gradient-to-b from-gray-50 to-white min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Validation de votre invitation...</p>
            </div>
          </main>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (errors.activation && !userData) {
    return (
      <ThemeProvider theme={CustomTheme}>
        <div className="min-h-screen bg-white">
          <Header />
          <main className="py-20 bg-gradient-to-b from-gray-50 to-white min-h-screen">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
              <Card className="border-2 border-red-200 bg-red-50 rounded-2xl shadow-xl">
                <CardContent className="p-12 text-center">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
                    <AlertCircle className="h-10 w-10 text-red-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-red-900 mb-4">
                    Invitation invalide
                  </h1>
                  <p className="text-red-800 mb-8 text-lg">
                    {errors.activation}
                  </p>
                  <Button
                    onClick={() => navigate("/")}
                    className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl"
                  >
                    Retour à l'accueil
                  </Button>
                </CardContent>
              </Card>
            </div>
          </main>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={CustomTheme}>
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          {success && (
            <section className="py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
              <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                <Card className="border-2 border-green-200 bg-green-50 rounded-2xl shadow-xl">
                  <CardContent className="p-12 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-green-900 mb-4">
                      Compte activé avec succès !
                    </h1>
                    <p className="text-green-800 mb-8 text-lg">
                      Votre compte a été activé. Vous pouvez maintenant vous
                      connecter à <strong>{userData?.entity.name}</strong>.
                    </p>
                    <div className="space-y-4">
                      <Button
                        onClick={() => navigate("/signin")}
                        className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl"
                      >
                        Se connecter maintenant
                      </Button>
                      <p className="text-sm text-green-700">
                        Utilisez votre adresse email et le mot de passe que vous
                        venez de définir.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          )}
          {!success && userData && (
            <section className="py-20 bg-gradient-to-b from-gray-50 to-white min-h-screen">
              <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                    Bienvenue dans{" "}
                    <span className="text-blue-600">Docuveritas</span>
                  </h1>
                  <p className="text-xl text-gray-600 mb-4">
                    Vous avez été invité à rejoindre{" "}
                    <strong>{userData.entity.name}</strong>
                  </p>
                  <p className="text-lg text-gray-500 mb-8">
                    Complétez votre profil pour activer votre compte
                  </p>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
                    <div className="flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-blue-600 mr-2" />
                      <span className="text-blue-800 font-medium">
                        Email : {userData.email}
                      </span>
                    </div>
                  </div>
                </div>

                <Card className="border-2 border-gray-200 rounded-2xl shadow-xl">
                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      Complétez votre profil
                    </CardTitle>
                    <p className="text-gray-600 mt-2">
                      Remplissez les informations ci-dessous pour activer votre
                      compte
                    </p>
                  </CardHeader>

                  <CardContent className="px-8 pb-8">
                    {errors.activation && (
                      <Alert className="mb-6 border-red-200 bg-red-50">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-red-700">
                          {errors.activation}
                        </AlertDescription>
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Personal Information */}
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <SelectLabeled
                              label="Titre"
                              value={inputTitle}
                              onChange={(e) => {
                                setInputTitle(e.target.value);
                                clearError("title");
                              }}
                              options={[
                                { value: "Mr.", label: "Mr." },
                                { value: "Mme", label: "Mme" },
                              ]}
                            />
                            {errors.title && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.title}
                              </p>
                            )}
                          </div>
                          <div>
                            <TextFieldLabeled
                              fullWidth
                              label="Prénom *"
                              name="firstName"
                              autoComplete="given-name"
                              value={inputFirstName}
                              onChange={(e) => {
                                setInputFirstName(e.target.value);
                                clearError("firstName");
                              }}
                              placeholder="Votre prénom"
                            />
                            {errors.firstName && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.firstName}
                              </p>
                            )}
                          </div>
                          <div>
                            <TextFieldLabeled
                              fullWidth
                              label="Nom *"
                              name="lastName"
                              autoComplete="family-name"
                              value={inputLastName}
                              onChange={(e) => {
                                setInputLastName(e.target.value);
                                clearError("lastName");
                              }}
                              placeholder="Votre nom"
                            />
                            {errors.lastName && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.lastName}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <TextFieldLabeled
                              fullWidth
                              type="tel"
                              label="Téléphone *"
                              name="phoneNumber"
                              autoComplete="tel"
                              value={inputPhoneNumber}
                              onChange={(e) => {
                                setInputPhoneNumber(e.target.value);
                                clearError("phoneNumber");
                              }}
                              placeholder="01 23 45 67 89"
                            />
                            {errors.phoneNumber && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.phoneNumber}
                              </p>
                            )}
                          </div>
                          <div>
                            <TextFieldLabeled
                              fullWidth
                              label="Profession *"
                              value={inputProfessionalPosition}
                              onChange={(e) => {
                                setInputProfessionalPosition(e.target.value);
                                clearError("professionalPosition");
                              }}
                              placeholder="Avocat d'affaires, Directeur financier, Juriste..."
                            />
                            {errors.professionalPosition && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.professionalPosition}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <AutocompleteInfiniteScroll<Territory>
                            loadOptions={fetchMoreData}
                            clearOnBlur={false}
                            initialOptions={territories}
                            hasMore={hasMore}
                            isLoading={isLoadingTerritories}
                            isOptionEqualToValue={(t, v) => t.id === v.id}
                            id="location-autocomplete"
                            label="Ville *"
                            value={inputLocation}
                            onChange={(_, v) => {
                              setInputLocation(v);
                              clearError("location");
                            }}
                            inputValue={inputLocationText}
                            onInputChange={(e, v) => setInputLocationText(v)}
                            getOptionLabel={(t) =>
                              `${t.name}, ${t.parents[0]?.parents[0]?.name}`
                            }
                            sx={{
                              "& .MuiInputBase-root": {
                                border: "2px solid #e5e7eb",
                                borderRadius: "12px",
                                "&:focus-within": {
                                  borderColor: "#64748b",
                                },
                              },
                            }}
                          />
                          {errors.location && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.location}
                            </p>
                          )}
                        </div>

                        {/* Security */}
                        <div className="border-t pt-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Sécurité du compte
                          </h3>

                          <div className="space-y-4">
                            <div>
                              <Label
                                htmlFor="password"
                                className="text-sm font-medium text-gray-700"
                              >
                                Mot de passe *
                              </Label>
                              <div className="relative mt-1">
                                <Input
                                  id="password"
                                  type={showPassword ? "text" : "password"}
                                  value={inputPassword}
                                  onChange={(e) => {
                                    setInputPassword(e.target.value);
                                    clearError("password");
                                  }}
                                  className="border-2 border-gray-200 focus:border-slate-500 rounded-xl pr-12"
                                  placeholder="Minimum 8 caractères"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  {showPassword ? (
                                    <EyeOff className="h-5 w-5" />
                                  ) : (
                                    <Eye className="h-5 w-5" />
                                  )}
                                </button>
                              </div>
                              {errors.password && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.password}
                                </p>
                              )}
                            </div>

                            <div>
                              <Label
                                htmlFor="confirmPassword"
                                className="text-sm font-medium text-gray-700"
                              >
                                Confirmer le mot de passe *
                              </Label>
                              <div className="relative mt-1">
                                <Input
                                  id="confirmPassword"
                                  type={
                                    showConfirmPassword ? "text" : "password"
                                  }
                                  value={inputConfirmPassword}
                                  onChange={(e) => {
                                    setInputConfirmPassword(e.target.value);
                                    clearError("confirmPassword");
                                  }}
                                  className="border-2 border-gray-200 focus:border-slate-500 rounded-xl pr-12"
                                  placeholder="Confirmer votre mot de passe"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  {showConfirmPassword ? (
                                    <EyeOff className="h-5 w-5" />
                                  ) : (
                                    <Eye className="h-5 w-5" />
                                  )}
                                </button>
                              </div>
                              {errors.confirmPassword && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.confirmPassword}
                                </p>
                              )}
                            </div>

                            <div>
                              <SelectLabeled
                                label="Langue préférée"
                                value={inputLanguage}
                                onChange={(e) =>
                                  setInputLanguage(e.target.value)
                                }
                                options={[
                                  { value: "fr", label: "Français" },
                                  { value: "en", label: "English" },
                                  { value: "ar", label: "العربية" },
                                ]}
                              />
                            </div>

                            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                              <h4 className="font-semibold text-gray-900 mb-3">
                                Critères de sécurité du mot de passe :
                              </h4>
                              <ul className="space-y-2 text-sm text-gray-600">
                                <li
                                  className={`flex items-center ${
                                    inputPassword.length >= 8
                                      ? "text-green-600"
                                      : ""
                                  }`}
                                >
                                  <CheckCircle
                                    className={`h-4 w-4 mr-2 ${
                                      inputPassword.length >= 8
                                        ? "text-green-600"
                                        : "text-gray-400"
                                    }`}
                                  />
                                  Au moins 8 caractères
                                </li>
                                <li
                                  className={`flex items-center ${
                                    /[A-Z]/.test(inputPassword)
                                      ? "text-green-600"
                                      : ""
                                  }`}
                                >
                                  <CheckCircle
                                    className={`h-4 w-4 mr-2 ${
                                      /[A-Z]/.test(inputPassword)
                                        ? "text-green-600"
                                        : "text-gray-400"
                                    }`}
                                  />
                                  Une lettre majuscule
                                </li>
                                <li
                                  className={`flex items-center ${
                                    /[0-9]/.test(inputPassword)
                                      ? "text-green-600"
                                      : ""
                                  }`}
                                >
                                  <CheckCircle
                                    className={`h-4 w-4 mr-2 ${
                                      /[0-9]/.test(inputPassword)
                                        ? "text-green-600"
                                        : "text-gray-400"
                                    }`}
                                  />
                                  Un chiffre
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div className="border-t pt-6">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              id="acceptTerms"
                              checked={inputAcceptTerms}
                              onCheckedChange={(checked) => {
                                setInputAcceptTerms(checked as boolean);
                                clearError("acceptTerms");
                              }}
                            />
                            <Label
                              htmlFor="acceptTerms"
                              className="text-sm text-gray-700 leading-relaxed"
                            >
                              J'accepte les{" "}
                              <a
                                href="/cgu"
                                className="text-slate-500 hover:underline"
                              >
                                Conditions Générales d'Utilisation
                              </a>{" "}
                              et la{" "}
                              <a
                                href="/confidentialite"
                                className="text-slate-500 hover:underline"
                              >
                                Politique de Confidentialité
                              </a>{" "}
                              de Docuveritas *
                            </Label>
                          </div>
                          {errors.acceptTerms && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.acceptTerms}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="pt-8 border-t border-gray-200">
                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="w-full bg-slate-500 hover:bg-slate-600 text-white py-3 rounded-xl text-lg font-medium"
                        >
                          {isLoading ? (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Activation en cours...</span>
                            </div>
                          ) : (
                            <>
                              Activer mon compte
                              <CheckCircle className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </ThemeProvider>
  );
};

export default InviteActivation;
